import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtINR, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Masters() {
  return (
    <div className="space-y-4" data-testid="masters-root">
      <Toaster position="top-center" />
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Masters</h1>
      <Tabs defaultValue="nozzles">
        <TabsList>
          <TabsTrigger value="nozzles" data-testid="tab-nozzles">Nozzles</TabsTrigger>
          <TabsTrigger value="pm" data-testid="tab-pm">Payment Modes</TabsTrigger>
          <TabsTrigger value="debtors" data-testid="tab-debtors">Debtors</TabsTrigger>
          <TabsTrigger value="prices" data-testid="tab-prices">Fuel Prices</TabsTrigger>
          <TabsTrigger value="lubs" data-testid="tab-lubs">Lubricants</TabsTrigger>
        </TabsList>
        <TabsContent value="nozzles"><Nozzles /></TabsContent>
        <TabsContent value="pm"><PMs /></TabsContent>
        <TabsContent value="debtors"><Debtors /></TabsContent>
        <TabsContent value="prices"><Prices /></TabsContent>
        <TabsContent value="lubs"><Lubs /></TabsContent>
      </Tabs>
    </div>
  );
}

function Nozzles() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/masters/nozzles").then((r) => setRows(r.data)); }, []);
  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm mt-3">
      <div className="overflow-x-auto">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Label</th><th>Fuel</th><th>DU Serial</th><th>DU Code</th><th>Cluster</th><th>Active</th></tr></thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.nozzle_id}>
                <td className="font-bold">{n.label}</td>
                <td>{n.fuel}</td>
                <td>{n.du_serial}</td>
                <td>{n.du_code}</td>
                <td>{n.cluster}</td>
                <td>{n.active ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PMs() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [imgUrl, setImgUrl] = useState("");

  const load = () => api.get("/masters/payment_modes").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (pm) => {
    setEditing(pm.pm_id);
    setImgUrl(pm.image_url || "");
  };
  const saveImg = async () => {
    try {
      await api.patch(`/masters/payment_modes/${editing}`, { image_url: imgUrl });
      // Reset the client-side cache so new hovers see the update
      const mod = await import("@/components/PaymentModeImage");
      mod.invalidatePaymentModesCache?.();
      setEditing(null);
      load();
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm mt-3">
      <div className="text-xs text-slate-500 mb-2">
        Paste an image URL for each payment mode (screenshot of the PhonePe / Paytm / ICICI QR settlement).
        Cashiers see it on hover, next to every amount, so they instantly recognise the slip.
      </div>
      <table className="dense-table w-full text-sm">
        <thead><tr><th>ID</th><th>Name</th><th>Scope</th><th>Ref image</th><th>Active</th><th></th></tr></thead>
        <tbody>
          {rows.map((n) => (
            <tr key={n.pm_id}>
              <td className="font-mono">{n.pm_id}</td>
              <td className="font-bold">{n.name}</td>
              <td>{n.category_scope}</td>
              <td>
                {n.image_url ? (
                  <a href={n.image_url} target="_blank" rel="noreferrer" className="underline text-slate-700">
                    <img src={n.image_url} alt={n.name} className="inline-block max-h-8 max-w-16 object-contain border border-slate-300 bg-white" />
                  </a>
                ) : (
                  <span className="text-slate-400 italic">none</span>
                )}
              </td>
              <td>{n.active !== false ? "✓" : "—"}</td>
              <td>
                {editing === n.pm_id ? (
                  <div className="flex gap-1">
                    <input
                      className="border border-slate-300 h-8 px-2 text-xs w-64"
                      value={imgUrl}
                      onChange={(e) => setImgUrl(e.target.value)}
                      placeholder="https://…"
                      data-testid={`pm-img-input-${n.pm_id}`}
                    />
                    <button
                      onClick={saveImg}
                      data-testid={`pm-img-save-${n.pm_id}`}
                      className="h-8 px-3 bg-slate-900 text-white text-xs font-bold rounded-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditing(null)}
                      className="h-8 px-3 border border-slate-300 text-xs font-bold rounded-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => startEdit(n)}
                    data-testid={`pm-img-edit-${n.pm_id}`}
                    className="h-8 px-3 border border-slate-300 text-xs font-bold rounded-sm hover:bg-slate-100"
                  >
                    {n.image_url ? "Change image" : "Add image"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Debtors() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/masters/debtors").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (id, status) => {
    await api.patch(`/masters/debtors/${id}`, { status: status === "Active" ? "Inactive" : "Active" });
    load();
  };
  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm mt-3">
      <table className="dense-table w-full text-sm">
        <thead><tr><th>ID</th><th>Name</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.map((n) => (
            <tr key={n.debtor_id}>
              <td className="font-mono">{n.debtor_id}</td>
              <td className="font-bold">{n.name}</td>
              <td>{n.status}</td>
              <td><Button size="sm" variant="outline" onClick={() => toggle(n.debtor_id, n.status)} className="text-xs h-7 border-slate-300">Toggle</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Prices() {
  const [rows, setRows] = useState([]);
  const [today, setToday] = useState({ date: todayISO(), hsd: 0, ms: 0, cng: 0 });
  const load = () => api.get("/masters/fuel_prices").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/masters/fuel_prices", today); toast.success("Saved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Set Price</h3>
        <div className="grid grid-cols-4 gap-2">
          <Input type="date" value={today.date} onChange={(e) => setToday({ ...today, date: e.target.value })} className="h-11 border border-slate-300" />
          <Input type="number" step="0.01" placeholder="HSD" value={today.hsd} onChange={(e) => setToday({ ...today, hsd: Number(e.target.value) })} className="h-11 num" />
          <Input type="number" step="0.01" placeholder="MS" value={today.ms} onChange={(e) => setToday({ ...today, ms: Number(e.target.value) })} className="h-11 num" />
          <Input type="number" step="0.01" placeholder="CNG" value={today.cng} onChange={(e) => setToday({ ...today, cng: Number(e.target.value) })} className="h-11 num" />
        </div>
        <Button className="mt-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={save} data-testid="save-price-btn">Save Prices</Button>
      </Card>
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Date</th><th>HSD</th><th>MS</th><th>CNG</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.date}>
                <td>{r.date}</td>
                <td className="num">{r.hsd}</td>
                <td className="num">{r.ms}</td>
                <td className="num">{r.cng}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Lubs() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/masters/lubricants").then((r) => setRows(r.data)); }, []);
  return (
    <Card className="p-3 border-2 border-slate-300 rounded-sm mt-3">
      <div className="overflow-x-auto">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>ID</th><th>Name</th><th>Sale ₹</th><th>Cost ₹</th></tr></thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.lub_id}>
                <td className="font-mono">{n.lub_id}</td>
                <td className="font-bold">{n.name}</td>
                <td className="num">{n.sale_price ?? "—"}</td>
                <td className="num">{n.purchase_price ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
